import { useEffect, useState } from 'react'
import { ExternalLink, FlaskConical, Wrench, Clock, Lock, ClipboardList, Trash2 } from 'lucide-react'
import { relabelBranch } from '../../lib/ticket.js'
import { cachedGet } from '../../lib/api.js'
import StagePicker from './StagePicker.jsx'
import LabInfoEditor from './LabInfoEditor.jsx'
import RetainerBuilder from './RetainerBuilder.jsx'
import RetainerDetail from './RetainerDetail.jsx'
import {
  Badge, Button, ConfirmDialog, Drawer, DrawerFooter, Field, Input, Select, Textarea,
  PatientLink, patientHref, fmtDate, fmtDateTime, fmtMoney, useToast,
} from '../../kit'
import {
  stageLog, stageStyle,
  labVendorName,
  labPatientName, labPatientHN, labPrice, labPriceClinic, labPriceDoctor,
  labPercentClinic, computeLabPrice, labMakerId, makerVisibleTo,
  STAGE_DEDUCTED, shownStage, isMaskedStageId,
} from '../../lib/lab.js'
import { useMe } from '../../lib/me.js'
import { labStageScope } from '../../lib/role.js'

// One lab record — detail drawer (right slide-in). Grouped: สถานะ → รายละเอียดงาน Lab →
// ข้อมูล Lab (เชื่อมโยง + vendor/date/doctor) → ค่า Lab → รายละเอียดรีเทนเนอร์ → ประวัติสถานะ.
//
// It OPENS IN EDIT MODE whenever the record is writable, with one บันทึก in the footer
// and one POST /lab behind it. Only สถานะ writes on its own — it is a workflow action,
// not a form field, and it appends its own stage-history entry.
//
// `onDelete(row)` is OPTIONAL and drives the ลบงาน Lab button in the footer. Omit it and
// no button renders — which is why /lab does not offer delete and /patient/:id/lab does:
// destroying a lab record is a decision made in front of the patient it belongs to, not
// while scanning the whole clinic's bench.
//
// `showPatientLink=false` when the drawer is already open ON that patient's page — both
// routes out of here (the ↗ on the name and the เปิดหน้าคนไข้ button) open /patient/:id in
// a new tab, which from /patient/:id/lab is a second tab of the page you are standing on.
// One prop governs both because they are the same action twice, not two features.
export default function LabDetailDrawer({ row, stages = [], branchName = {}, hideMoney = false, priceLocked = false, onSave, onDelete, onClose, onChanged, beforeStageChange, readOnlyDetail = false, labOnly = false, showPatientLink = true }) {
  // The drawer OPENS in edit mode (2026-08-17, Ram). Staff open a lab to change something
  // — a vendor, a date, a price — so a read-only first screen was a click of ceremony in
  // front of every one of those. `editing` only goes false for a record that may not be
  // written: stage 8, or a Lab-only technician. There is no แก้ไข button any more.
  const [editing, setEditing] = useState(true)
  const me = useMe()
  const [detailVal, setDetailVal] = useState('')
  const [pv, setPv] = useState('')
  const [pct, setPct] = useState('')
  const [pctManual, setPctManual] = useState(false)
  // What the embedded LabInfoEditor currently holds. It owns those fields; the drawer
  // only needs their value at save time and whether they are usable.
  // What the embedded LabInfoEditor currently holds. It owns those fields; the drawer only
  // needs their value at save time and whether they are usable. Starts invalid so บันทึก is
  // never live for the tick before the editor's first report, which would send the detail
  // and price and nothing else.
  //
  // `forRow` is not paranoia — the drawer stays MOUNTED across openRow changes, so without
  // it a click could commit the previous lab's vendor and date onto the lab now on screen.
  const [info, setInfo] = useState({ fields: null, valid: false, forRow: null })
  // production_type + retainer, set by RetainerBuilder. null = untouched by this session,
  // so the save leaves both columns alone.
  const [spec, setSpec] = useState(null)
  const [specOpen, setSpecOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState('')

  // What the drawer's own fields were seeded with, so it knows when they hold unsaved
  // edits (the Drawer then asks "ทิ้งการแก้ไข?" before closing on Esc / backdrop / ✕).
  const [seed, setSeed] = useState({ pv: '', pct: '' })
  const toast = useToast()

  const [confirmDel, setConfirmDel] = useState(false)
  const [delBusy, setDelBusy] = useState(false)
  const [delErr, setDelErr] = useState('')

  // Seed the form from the record, and re-seed whenever the drawer is pointed at a
  // different lab. Before edit-by-default this lived in a startEdit() click handler;
  // there is no click any more, so it is an effect keyed on the row.
  const rowId = row?.id
  useEffect(() => {
    if (!row) return
    setDetailVal(row.detail || '')
    // % หัก LAB (clinic share) — seed from the record, else the amount ratio, else 50.
    const t = labPrice(row)
    const p = labPercentClinic(row) || (t > 0 ? Math.round(labPriceClinic(row) / t * 100) : 50)
    setPv(String(t)); setPct(String(p)); setPctManual(!['0', '50', '75', '100'].includes(String(p)))
    setSeed({ pv: String(t), pct: String(p) })
    setSpec(null); setSaveErr('')
    // ⚠️ Do NOT reset `info` here. React runs CHILD effects before parent effects, so the
    // embedded LabInfoEditor has ALREADY reported its fields by the time this runs — a
    // reset wipes that report, and the child's dedupe guard (same signature) never re-sends
    // it, leaving บันทึก disabled forever. That was the 2026-08-18 bug. `key={rowId}` on the
    // editor plus the `forRow` stamp do this job instead: the child remounts per lab and
    // re-reports on its own.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId])

  if (!row) return null

  const priceValid = Number.isFinite(Number(pv)) && Number(pv) >= 0 && Number.isFinite(Number(pct)) && Number(pct) >= 0 && Number(pct) <= 100
  const priceCalc = computeLabPrice(pv || 0, pct || 0)

  const stagesById = Object.fromEntries(stages.map((s) => [Number(s.id), s]))
  // ประวัติสถานะ under the same role mask as the picker (lib/lab.js §stage visibility): for
  // normal staff a 3 → 4 run reads as ONE "อยู่ที่ LAB" entry, stamped when it first got there.
  const stageScope = labStageScope(me)
  const timeline = stageLog(row).filter((e, i, all) => !(i > 0 && isMaskedStageId(e.labstage_id, stageScope) && isMaskedStageId(all[i - 1].labstage_id, stageScope)))
  const stageName = row.labstage?.name || stagesById[Number(row.labstage_id)]?.name || ''
  // maker is an Employee id; resolve it to a code for display. Only fetched when a
  // maker is actually shown, so the list costs nothing on a normal lab.
  // The active roster — used both for the ผู้ทำ label and to turn the stage history's
  // employee ids into codes ("โดย #162" is meaningless to the person reading it).
  const [staff, setStaff] = useState(null)
  const showMaker = makerVisibleTo(row, { labOnly })
  useEffect(() => {
    let alive = true
    cachedGet('/api/lab/makers').then((l) => alive && setStaff(Array.isArray(l) ? l : [])).catch(() => {})
    return () => { alive = false }
  }, [])
  const staffCode = (id) => {
    const n = Number(id) || 0
    if (!n) return ''
    const hit = (staff || []).find((m) => Number(m.id) === n)
    return hit ? (hit.code || hit.name || `#${n}`) : `#${n}`
  }
  const makerLabel = staffCode(labMakerId(row))

  // Everything on a lab stays editable until the doctor deduction is settled (Ram,
  // 2026-08-17). หักยอดทันตแพทย์แล้ว means the cost has ALREADY come out of a doctor's
  // salary, so from there the record is frozen whole — detail, retainer, ข้อมูล Lab, ค่า
  // Lab, stage and delete. It used to be far tighter (รอส่ง LAB only), which made a typo
  // in the job sheet permanent the moment the piece was sent to the lab.
  //
  // A lab technician still sees the full spec but may only move the stage — the job
  // sheet is the counter's record, not theirs to rewrite.
  const stageLocked = stageName === STAGE_DEDUCTED
  const canEdit = !readOnlyDetail && !stageLocked && !!onSave
  // A record that may not be written is simply read, whatever `editing` says.
  const inEdit = editing && canEdit
  // The embedded editor has reported usable fields FOR THIS LAB. One derived gate, used by
  // the button and re-checked by the write, so the two cannot disagree.
  const infoReady = info.valid && info.forRow === rowId && !!info.fields
  const pid = Number(row.patient_id) || 0
  const canOpenPatient = showPatientLink && pid > 0
  const patientUrl = canOpenPatient ? patientHref(pid, { context: 'lab' }) : ''
  const branch = branchName[String(row.branch_id)] ? relabelBranch(branchName[String(row.branch_id)]) : (row.branch_id ? `#${row.branch_id}` : '—')


  // The one write. Everything the drawer can change goes up in a single POST /lab, so a
  // vendor change cannot land while the price edit beside it fails — see
  // functions/api/lab/edit.js for why that is one call rather than three.
  async function commit() {
    if (!canCommit) return
    setSaving(true); setSaveErr('')
    const payload = { detail: detailVal, ...(info.fields || {}) }
    if (!hideMoney && !priceLocked) payload.price = priceCalc
    // 🚨 `retainer` and `production_type` are ALWAYS sent, even untouched. Upstream reads
    // `($input.retainer|is_empty) ? $_lab.retainer : (…|set every subfield…)`, which LOOKS
    // like "omit = preserve" — but the dblink materialises an absent `retainer` as an
    // object of empty defaults, which is not `is_empty`, so the second branch runs and
    // blanks the lot. Proven on lab 41118 (2026-08-17 18:00 UTC): a price-only save erased
    // a retainer written 25 seconds earlier, leaving every subfield empty except
    // `employee_id` — the only one carrying a first_notempty fallback.
    // Same shape of trap as `month`; see functions/api/lab/edit.js.
    payload.production_type = spec ? spec.production_type : (row.production_type || '')
    payload.retainer = spec ? spec.retainer : retainerForWrite(row.retainer)
    try {
      await onSave(row, payload)
      setEditing(false); setSpec(null)
      toast.success('บันทึกแล้ว')
    } catch (e) {
      setSaveErr(e?.body?.detail || e?.message || 'บันทึกไม่สำเร็จ')
      toast.error('บันทึกไม่สำเร็จ', { detail: e?.body?.detail || e?.message })
    } finally { setSaving(false) }
  }

  const canCommit = infoReady && !saving && (hideMoney || priceLocked || priceValid)

  // What the two spec fields should read as: this session's unsaved pick if there is
  // one, else the record. Keeps the summary honest while the drawer is mid-edit.
  const shownProductionType = spec ? spec.production_type : row.production_type
  const shownRetainer = spec ? spec.retainer : row.retainer

  // Unsaved edits the drawer itself holds. LabInfoEditor's fields (vendor, date, links…)
  // are not compared: the editor owns them and reports no baseline — see the kit-gap note.
  const dirty = inEdit && (
    detailVal !== (row.detail || '')
    || !!spec
    || (!hideMoney && !priceLocked && (pv !== seed.pv || pct !== seed.pct))
  )
  const PCT_OPTIONS = [
    { value: '0', label: '0%' }, { value: '50', label: '50%' }, { value: '75', label: '75%' },
    { value: '100', label: '100%' }, { value: '__m', label: 'กำหนดเอง…' },
  ]

  const patientName = labPatientName(row)

  // ONE footer. The drawer is always in edit mode when the record is writable, so the
  // cancel button IS the way out (ยกเลิก while editing, ปิด on a read-only record).
  // ลบ stays reachable throughout: it is not an escape hatch from the form, it is the
  // other thing you might have opened this record to do.
  const footer = (
    <DrawerFooter
      onCancel={onClose}
      cancelLabel={inEdit ? 'ยกเลิก' : 'ปิด'}
      onSave={inEdit ? commit : undefined}
      saving={saving}
      canSave={canCommit}
      status={delErr || saveErr}
      statusTone="error"
      // Hidden, not disabled, once the deduction is settled: there is nothing the
      // user could do to make it work, and a greyed button invites the attempt.
      danger={onDelete && !stageLocked ? (
        <Button variant="danger-soft" icon={Trash2} loading={delBusy} disabled={saving}
          onClick={() => { setDelErr(''); setConfirmDel(true) }}>ลบ</Button>
      ) : null}
      secondary={canOpenPatient ? (
        <Button variant="secondary" icon={ExternalLink} onClick={() => window.open(patientUrl, '_blank', 'noopener')}>เปิดหน้าคนไข้</Button>
      ) : null}
    />
  )

  return (
    <Drawer
      onClose={onClose}
      width="sm"
      icon={FlaskConical}
      title={canOpenPatient
        ? <PatientLink patientId={pid} context="lab" icon>{patientName}</PatientLink>
        : patientName}
      subtitle={(
        <span className="flex items-center gap-1.5 flex-wrap">
          {labPatientHN(row) && <span>HN {labPatientHN(row)}</span>}
          <span>· Lab #{row.id}</span>
          {row.repair && <Badge tone="amber"><Wrench size={10} /> งานแก้</Badge>}
        </span>
      )}
      busy={saving || delBusy}
      dirty={dirty}
      record={row}
      recordTable="lab"
      footer={footer}
    >
        <div className="space-y-5">
          {/* 1 · Stage */}
          <Field label="สถานะ"><StagePicker row={row} stages={stages} onChanged={onChanged} beforeChange={beforeStageChange} /></Field>

          {/* ผู้ทำ — captured when the technician marks the piece finished, so it only
              becomes meaningful (and visible to everyone else) from ทำเสร็จ onwards. */}
          {showMaker && (
            <Field label="ผู้ทำ">
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[13px] text-slate-700">
                <ClipboardList size={13} className="text-slate-400" />
                {makerLabel}
              </span>
            </Field>
          )}

          {/* 2 · Lab info — the links, the vendor, the date, the doctor */}
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[12px] font-medium uppercase tracking-wide text-slate-400">ข้อมูล Lab</span>
            </div>
            {inEdit ? (
              /* No onSave/onCancel: in edit mode this reports its fields upward and the
                 drawer's single บันทึก sends them. Passing onFieldsChange is what
                 suppresses its own button pair. */
              <LabInfoEditor
                key={rowId}
                row={row}
                patientId={pid}
                patientName={labPatientName(row)}
                patientHN={labPatientHN(row)}
                onFieldsChange={(fields, valid) => setInfo({ fields, valid, forRow: rowId })}
              />
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                <Field label="Lab / ผู้ผลิต">
                  <span className="inline-flex items-center gap-1.5 text-[14px] text-slate-800"><FlaskConical size={14} className="text-slate-400 shrink-0" /><span className="truncate">{labVendorName(row)}</span></span>
                </Field>
                <Field label="สาขา"><span className="text-[14px] text-slate-800">{branch}</span></Field>
                <Field label="วันที่"><span className="text-[14px] text-slate-800 tabular-nums">{fmtDate(row.date)}</span></Field>
                {/* กำหนดส่ง is now a field in its own right, not a footnote to วันที่ — it is
                    the date staff choose (and the in-house bench's quota day), while วันที่
                    follows the linked นัดหมาย (Ram, 2026-09-07). Still hidden when it holds
                    nothing to say: 0 on the old imports, and printing the same day twice under
                    two labels teaches the reader that the two are the same thing. */}
                {Number(row.deadline) > 0 && Number(row.deadline) !== Number(row.date) && (
                  <Field label="กำหนดส่ง">
                    <span className="text-[14px] text-slate-800 tabular-nums">{fmtDate(row.deadline)}</span>
                    {Number(row.deadline) < Number(row.date) && (
                      <span className="ml-1.5 text-[11.5px] text-slate-400">· ส่งก่อนวันนัด</span>
                    )}
                  </Field>
                )}
              </div>
            )}
          </section>

          {/* 3 · Lab detail — the free-text job note. It sits BELOW ข้อมูล Lab (2026-08-17):
              the note describes a piece you have already said which visit, vendor and
              date it belongs to, so it reads as the last thing about the order, not the
              first thing about the record. */}
          <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-medium uppercase tracking-wide text-slate-400">รายละเอียดงาน Lab</span>
              <span className="text-[12px] text-slate-500 truncate">ประเภท: <span className="text-slate-700 font-medium">{shownProductionType || '—'}</span>{spec && <span className="text-brand-500"> ·แก้ไขแล้ว</span>}</span>
            </div>
            {inEdit ? (
              <Textarea value={detailVal} onChange={(e) => setDetailVal(e.target.value)} rows={3} disabled={saving} placeholder="เพิ่มรายละเอียดงาน…" className="resize-y" />
            ) : (
              <div className={`w-full rounded-lg px-3 py-2 text-[13.5px] whitespace-pre-wrap bg-slate-50 ${row.detail ? 'text-slate-700' : 'text-slate-400'}`}>
                {row.detail || '—'}
              </div>
            )}
            {!canEdit && <div className="text-[12px] text-slate-400 flex items-center gap-1"><Lock size={10} /> {readOnlyDetail ? 'ดูได้อย่างเดียว — เปลี่ยนได้เฉพาะสถานะ' : `${STAGE_DEDUCTED} — แก้ไขไม่ได้`}</div>}
          </section>

          {/* 4 · ค่า Lab */}
          {!hideMoney && (
          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[12px] font-medium uppercase tracking-wide text-slate-400">ค่า Lab</span>
              {priceLocked && <span className="inline-flex items-center gap-1 text-[12px] text-slate-400" title="ล็อกแล้ว (หักยอดทันตแพทย์แล้ว)"><Lock size={11} /> ล็อกแล้ว</span>}
            </div>
            {inEdit && !priceLocked ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="ค่า Lab รวม">
                    <Input value={pv} onChange={(e) => setPv(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" disabled={saving}
                      className="font-semibold tabular-nums" />
                  </Field>
                  <Field label="% หัก LAB">
                    {pctManual ? (
                      <div>
                        <div className="relative">
                          <Input value={pct} onChange={(e) => setPct(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" disabled={saving} placeholder="0–100"
                            className="pr-6 font-semibold tabular-nums" />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[12px] text-slate-400">%</span>
                        </div>
                        <button type="button" onClick={() => setPctManual(false)} className="mt-1 text-[12px] text-slate-400 hover:text-brand-600">↩ เลือกจากรายการ</button>
                      </div>
                    ) : (
                      <Select
                        value={['0', '50', '75', '100'].includes(String(pct)) ? String(pct) : '50'}
                        onChange={(v) => { if (v === '__m') setPctManual(true); else if (v !== '') setPct(String(v)) }}
                        disabled={saving}
                        options={PCT_OPTIONS}
                      />
                    )}
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[13px]">
                  <CalcTile label="หักคลินิก" pct={priceCalc.percentClinic} value={priceCalc.priceClinic} />
                  <CalcTile label="หักทันตแพทย์" pct={priceCalc.percentDoctor} value={priceCalc.priceDoctor} />
                </div>
                {!priceValid && <div className="text-[12px] text-amber-600">กรอกค่า Lab และ % หัก LAB (0–100) ให้ถูกต้อง</div>}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 text-[13px]">
                <StatTile label="ค่า Lab รวม" value={fmtMoney(labPrice(row), { decimals: 2 })} strong />
                <StatTile label="% หัก LAB" value={`${labPercentClinic(row)}%`} />
                <StatTile label="หักคลินิก" value={fmtMoney(labPriceClinic(row), { decimals: 2 })} />
                <StatTile label="หักทันตแพทย์" value={fmtMoney(labPriceDoctor(row), { decimals: 2 })} />
              </div>
            )}
          </section>
          )}

          {/* 5 · รายละเอียดรีเทนเนอร์ — the spec, read out.
              The นัดหมาย and การรักษา sections that used to sit here are GONE (2026-08-17):
              the เชื่อมโยง pickers inside ข้อมูล Lab already render both records in full —
              date, doctor, branch, type, note — so these were the same two facts twice,
              lower down and less useful. Removing them also dropped four fetches per
              drawer open (/api/appointment/:id, /api/treatment/:id, appointment-types,
              doctors). What was NOT visible anywhere was the retainer spec, which lived
              only behind the builder button; that is what took their place. */}
          <RetainerDetail
            productionType={shownProductionType}
            retainer={shownRetainer}
            dirty={!!spec}
            onEdit={inEdit ? () => setSpecOpen(true) : null}
          />

          {/* 6 · Stage history */}
          <section className="border-t border-slate-200 pt-4">
            <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><Clock size={12} /> ประวัติสถานะ</div>
            {timeline.length === 0 ? (
              <div className="text-[13px] text-slate-400">ยังไม่มีการบันทึกการเปลี่ยนสถานะ</div>
            ) : (
              <ol className="space-y-3">
                {timeline.map((e, i) => {
                  const s = shownStage(e.labstage_id, stages, stageScope) || {}
                  const ss = stageStyle(s.color)
                  return (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="mt-1 w-2.5 h-2.5 rounded-full shrink-0" style={ss.dot} />
                      <div className="min-w-0">
                        <div className="text-[13.5px] text-slate-800">{s.name || (e.labstage_id ? `สถานะ #${e.labstage_id}` : e.type || 'เปลี่ยนสถานะ')}</div>
                        <div className="text-[12px] text-slate-400">{fmtDateTime(e.timestamp)}{e.employee_id ? ` · โดย ${staffCode(e.employee_id)}` : ''}</div>
                      </div>
                    </li>
                  )
                })}
              </ol>
            )}
          </section>

        </div>

      {/* The drawer owns this now (it used to be hoisted to each page via onEditRetainer).
          In edit mode it does not write — it hands its config back and the drawer's one
          บันทึก carries it, so the retainer cannot save while the vendor beside it fails. */}
      {specOpen && (
        <RetainerBuilder
          row={{ ...row, production_type: shownProductionType, retainer: shownRetainer }}
          onSave={async (_r, next) => { setSpec(next) }}
          onClose={() => setSpecOpen(false)}
        />
      )}

      {/* Never window.confirm — house rule. `tone="danger"` because this one really is:
          Xano's DELETE /lab/{lab_id} is a hard db.del, so the price split and the whole
          stage history go with the row. The dialog says that in as many words. */}
      {confirmDel && (
        <ConfirmDialog
          tone="danger"
          title="ลบงาน Lab นี้?"
          message={(
            <>
              <span className="block text-slate-800 font-medium">{labTitleOf(row)} · {labVendorName(row)}</span>
              <span className="block mt-1">ลบแล้วกู้คืนไม่ได้ — ค่า Lab และประวัติสถานะจะหายไปด้วย</span>
            </>
          )}
          confirmLabel="ลบงาน Lab"
          busy={delBusy}
          onCancel={() => setConfirmDel(false)}
          onConfirm={async () => {
            // The dialog stays up (busy) while the DELETE runs, so a second click cannot
            // send it twice. On success the page closes the whole drawer; on failure the
            // dialog closes so the footer's error line is readable.
            setDelBusy(true); setDelErr('')
            try { await onDelete(row); setConfirmDel(false) } catch (e) {
              setConfirmDel(false)
              setDelErr(e?.body?.detail || e?.message || 'ลบไม่สำเร็จ'); setDelBusy(false)
              toast.error('ลบไม่สำเร็จ', { detail: e?.body?.detail || e?.message })
            }
          }}
        />
      )}
    </Drawer>
  )
}

// The full retainer object, in the shape POST /lab's |set chain expects, defaulted so a
// record that has never been specced round-trips as empties instead of undefineds.
// `maker` is deliberately absent: the upstream chain never |sets it, so it cannot be
// preserved from here — see the note in project.md.
function retainerForWrite(r) {
  const x = r || {}
  const rub = x.retainer_rubber || {}
  return {
    retainer_upper: !!x.retainer_upper,
    retainer_lower: !!x.retainer_lower,
    retainer_type: x.retainer_type || '',
    retainer_preset: x.retainer_preset || '',
    retainer_pattern: x.retainer_pattern || '',
    retainer_color: Array.isArray(x.retainer_color) ? x.retainer_color : [],
    retainer_addon: Array.isArray(x.retainer_addon) ? x.retainer_addon : [],
    retainer_note: x.retainer_note || '',
    retainer_rubber: {
      upper: !!rub.upper, lower: !!rub.lower,
      upper_color: rub.upper_color || '', lower_color: rub.lower_color || '',
    },
  }
}

// The drawer's own one-line name for the piece, for the delete confirmation. Same
// precedence as lib/lab.js#labTitle without importing the retainer summary machinery:
// `detail` is the only field that actually distinguishes one appliance from another.
function labTitleOf(row) {
  const first = String(row?.detail || '').split('\n').map((s) => s.trim()).find(Boolean)
  return first || row?.production_type || (row?.id ? `Lab #${row.id}` : 'งาน Lab')
}

function StatTile({ label, value, strong }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-slate-400 text-[12px]">{label}</div>
      <div className={`tabular-nums ${strong ? 'text-[16px] font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>{value}</div>
    </div>
  )
}
function CalcTile({ label, pct, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-slate-400 text-[12px]">{label} · {pct}%</div>
      <div className="font-semibold text-slate-700 tabular-nums">{fmtMoney(value, { decimals: 2 })}</div>
    </div>
  )
}
